import * as Sentry from '@sentry/react';
import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { ExpertRequestType } from '@/__generated__/graphql';
import { NewCustomerSignupModalOTP as NewCustomerSignupModal } from '@/auth/components/NewCustomerSignupModal';
import { rateTypeForBillingAccount, useCustomerRateType } from '@/core/customer';
import { resolveActiveContextGroup, resolveScopedBillingGroup } from '@/core/user';
import { useAppSelector } from '@/store';

import RequestConsultation from '../RequestConsultation';
import SMBConsultationRequest from '../SMBConsultationRequest';
import TeamAccountPromoDialog from '../TeamAccountPromo/TeamAccountPromoDialog';

export interface ConsultationRequestRouterProps {
  open: boolean;
  onClose?: () => void;
  expert: {
    id: string;
    name: string;
    slug?: string;
    title?: string;
    pictureUrl?: string;
    timezone?: string;
    // Mirrors PROFILE_FIELDS (src/profile/store/queries.ts) — client_rate and credit_rate are
    // nullable in the schema and mean "the API could not price this expert". Forwarded to
    // SMBConsultationRequest / NewCustomerSignupModal, which say so rather than quoting a
    // substituted figure (ENG-5571).
    clientRate?: number | null;
    creditRate?: number | null;
  };
  // Props passed through to RequestConsultation for credits flow
  profile?: any;
  expertId?: string;
  expertRequest?: any;
  // ENG-5506: a team the booking was launched for (e.g. the team that owns the network
  // an invited expert joined). Wins over the active view-as account when the viewer is a
  // member and it has billing; an expert request's account still wins over it.
  groupId?: string;
  returnTo?: string;
}

/**
 * Routes consultation requests based on customer rate type.
 *
 * Scenarios:
 * 1. USD customer with billing (isSMBAccount=true) → SMBConsultationRequest (dollar pricing)
 * 2. Credits customer → Credits flow (RequestConsultation)
 * 3. USD customer without billing (signup_type='smb', pending) → NewCustomerSignupModal
 * 4. No rate type → TeamAccountPromoDialog (Contact Sales)
 */
function ConsultationRequestRouter({
  open,
  onClose,
  expert,
  profile,
  expertId,
  expertRequest,
  groupId,
  returnTo,
}: ConsultationRequestRouterProps): JSX.Element | null {
  const viewer = useAppSelector((state) => state.viewer);
  const userContext = useAppSelector((state) => state.ui.userContext);
  const navigate = useNavigate();
  const viewerRateType = useCustomerRateType();

  const handleClose = useCallback(() => {
    if (returnTo) navigate(returnTo, { replace: true });
    onClose?.();
  }, [returnTo, navigate, onClose]);

  // Viewer-wide classification — the fallback when there is no active group context
  // (personal / expert / admin / stale). Preserves pending-SMB signup ('usd', no
  // billing → signup modal) and 'none' (Contact Sales) routing.
  const smbGroup = viewer?.groups?.find((g) => g?.billing_account?.isSMBAccount === true);
  const viewerBillingGroup = smbGroup ?? viewer?.groups?.find((g) => g?.billing_account);
  const viewerBillingAccount = viewerBillingGroup?.billing_account;

  // ENG-5351: for a non-ER request, follow the ACTIVE view-as account, not the
  // all-groups "first SMB wins" scan above. When the active account is billable it is
  // authoritative; otherwise defer to the viewer-wide classification. This routes a
  // multi-account user by the account they selected instead of always their SMB group.
  // ENG-5506: a billable team the booking was launched for takes the active account's place.
  const activeGroup =
    resolveScopedBillingGroup(viewer, groupId) ?? resolveActiveContextGroup(viewer, userContext);
  const activeBillingAccount = activeGroup?.billing_account;
  // A pending SMB team (planType 'smb', billing not yet provisioned) classifies as
  // 'usd' even without a billing account — matches SelectType's entitlement signal, so
  // a user whose active context is pending-SMB isn't silently billed to another team.
  const activeIsPendingSMB = !activeBillingAccount && activeGroup?.capabilities?.planType === 'smb';
  const nonErRateType = activeBillingAccount
    ? rateTypeForBillingAccount(activeBillingAccount)
    : activeIsPendingSMB
      ? 'usd'
      : viewerRateType;
  const nonErBillingAccount = activeBillingAccount ?? viewerBillingAccount;
  const nonErGroupId = activeBillingAccount ? activeGroup?.id : viewerBillingGroup?.id;

  // ENG-5490: an ER-originated booking is billed to the ER's account (the backend
  // resolves the charge by ER/group), so currency, billing account, and group must
  // follow the ER — not the viewer's memberships (whose all-groups SMB scan would
  // mis-route a multi-account user). Mirrors CandidateWidgetHorizontal (the ER-page
  // path). Falls back to the viewer scan when there is no ER or the ER carries no
  // billing account (e.g. an inaccessible ER whose fetch was swallowed upstream).
  const erBillingAccount = expertRequest?.billing_account;
  const isErScoped = Boolean(erBillingAccount);

  const rateType = isErScoped ? rateTypeForBillingAccount(erBillingAccount) : nonErRateType;
  const billingAccount = isErScoped ? erBillingAccount : nonErBillingAccount;
  // For an ER-scoped booking use the ER's own group only — never fall back to the
  // viewer's group, which for a multi-account user is the SMB group and would tag a
  // credit booking with an SMB group_id (matches CandidateWidgetHorizontal, which has
  // no viewer fallback). The backend bills the ER's account regardless of this id.
  const billingGroupId = isErScoped ? expertRequest?.project?.group?.id : nonErGroupId;

  // ENG-5490: SMB accounts are consultation-only (ENG-5351), and a data check found
  // zero SMB expert requests with a non-consultation er_type. The SMB form hardcodes a
  // Consultation engagement, so an SMB ER of another type would be silently mis-booked
  // as a call. This should be unreachable — alert instead of coercing silently if it
  // ever happens (e.g. if SMB entitlements later expand).
  useEffect(() => {
    if (
      isErScoped &&
      rateType === 'usd' &&
      expertRequest?.er_type &&
      expertRequest.er_type !== ExpertRequestType.Consultation
    ) {
      Sentry.captureMessage(
        'SMB expert request with non-consultation er_type reached the request-call router',
        {
          level: 'warning',
          extra: { expertRequestId: expertRequest?.id, erType: expertRequest.er_type },
        }
      );
    }
  }, [isErScoped, rateType, expertRequest?.er_type, expertRequest?.id]);

  // Scenario 1: USD customer with billing → SMB consultation flow
  if (rateType === 'usd' && billingAccount) {
    return (
      <SMBConsultationRequest
        open={open}
        onClose={handleClose}
        expert={expert}
        billingAccount={billingAccount}
        groupId={billingGroupId}
        expertRequest={expertRequest}
      />
    );
  }

  // Scenario 2: Credits customer → Credits flow. Seed the billing group ONLY when it is
  // authoritative — an ER-scoped booking, or an active view-as group that is itself
  // billable. Otherwise pass undefined so the "Add to team" field stays Required and the
  // user can't submit a booking pre-billed to a team they never chose (ENG-5351).
  if (rateType === 'credits') {
    return (
      <RequestConsultation
        open={open}
        onClose={onClose}
        profile={profile || expert}
        expertId={expertId || expert.id}
        expertRequest={expertRequest}
        groupId={isErScoped || activeBillingAccount ? billingGroupId : undefined}
        returnTo={returnTo}
      />
    );
  }

  // Scenario 3: USD customer without billing → Show signup modal
  if (rateType === 'usd') {
    const expertWithSlug = {
      ...expert,
      slug: expert.slug || expert.id,
    };

    const group = viewerBillingGroup ?? viewer.groups?.[0];
    const authenticatedViewer = {
      id: viewer.id,
      timezone: viewer.timezone,
      groupName: group?.name,
      groupId: group?.id,
      hasBillingAccount: false,
      profileName:
        viewer.profile?.name ||
        `${viewer.profile?.first_name || ''} ${viewer.profile?.last_name || ''}`.trim() ||
        undefined,
    };

    return (
      <NewCustomerSignupModal
        open={open}
        onClose={handleClose}
        onComplete={handleClose}
        expert={expertWithSlug}
        authenticatedViewer={authenticatedViewer}
        landingUrl={typeof window !== 'undefined' ? window.location.href : undefined}
      />
    );
  }

  // Scenario 4: No rate type → Contact Sales
  if (viewer) {
    return (
      <TeamAccountPromoDialog
        open={open}
        onClose={handleClose}
        showEventTrack="promo.show.team_account.consultation_request"
      />
    );
  }

  return null;
}

export default ConsultationRequestRouter;
